import { useEffect, useState } from "react";
import { Input, Typography } from "antd";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { formatDateText } from "@/utils/dateUtils";

const { Title } = Typography;
const { TextArea } = Input;
const COMMENT_MAX_LENGTH = 200;

type DrawerComment = {
  author: string | null;
  text: string;
  createdAt: string;
};

type TasksCommentsSectionProps = {
  currentUserName: string | null;
  taskId: string | null | undefined;
};

export default function TasksCommentsSection({
  currentUserName,
  taskId,
}: TasksCommentsSectionProps) {
  const [commentDraft, setCommentDraft] = useState("");
  const [comments, setComments] = useState<DrawerComment[]>([]);

  useEffect(() => {
    setCommentDraft("");
    setComments([]);
  }, [taskId]);

  const handleAddComment = () => {
    const trimmedComment = commentDraft.trim();

    if (!trimmedComment) {
      return;
    }

    setComments((currentComments) => [
      {
        author: currentUserName,
        text: trimmedComment,
        createdAt: formatDateText(new Date().toISOString()),
      },
      ...currentComments,
    ]);
    setCommentDraft("");
  };

  const handleCommentKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();

      if (commentDraft.trim()) {
        handleAddComment();
      }
    }
  };

  return (
    <section>
      <Title level={5}>Comments</Title>
      <div className="rounded-sm py-4">
        <div className="flex items-start gap-3">
          <AvatarWithInitials
            name={currentUserName}
            avatarSize={32}
            showLabel={false}
          />
          <div className="flex-1">
            <TextArea
              value={commentDraft}
              onChange={(event) => setCommentDraft(event.target.value)}
              onKeyDown={handleCommentKeyDown}
              maxLength={COMMENT_MAX_LENGTH}
              placeholder="Add a comment and press Enter"
              rows={4}
              className="rounded-md"
              style={{ resize: "none" }}
            />
          </div>
        </div>
        {comments.length > 0 ? (
          <div className="mt-2 flex flex-col gap-4 border-t border-solid border-[#F0F0F0]">
            {comments.map((comment, index) => (
              <div
                key={`${taskId ?? "task"}-comment-${index}`}
                className="flex items-start gap-3"
              >
                <AvatarWithInitials
                  name={comment.author}
                  avatarSize={32}
                  showLabel={false}
                />
                <div className="flex-1 pt-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-black">
                      {comment.author ?? "Unknown User"}
                    </span>
                    <span className="text-xs text-[#8C8C8C]">
                      {comment.createdAt}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-[#2B4752]">
                    {comment.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
